import React from "react";
import { PushMessage } from "@myapp/contract";
import { usePushMessagesInfiniteQuery } from "../../../entities/notification";
import { describePushTarget } from "../lib/describePushTarget";

const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

/** "Hans Meier, Petra Klein +3" — two names carry the story, the rest is a count. */
const formatRecipients = (recipients: PushMessage["recipients"]): string => {
  const names = recipients.map((recipient) => recipient.name);
  const shown = names.slice(0, 2).join(", ");
  return names.length > 2 ? `${shown} +${names.length - 2}` : shown;
};

/**
 * A quiet log, not a second content column: plain rows with dividers, no cards, no brand
 * blue — the compose form above must stay the loudest thing in this pane.
 */
const MessageRow: React.FC<{ message: PushMessage }> = ({ message }) => (
  <div className="py-3">
    <div className="flex items-baseline justify-between gap-3">
      <p className="truncate text-[14px] font-['PT_Sans'] font-bold text-gray-700">
        {message.title}
      </p>
      <p className="shrink-0 text-[12px] font-['PT_Sans'] text-gray-400">
        {formatDateTime(message.createdAt)}
      </p>
    </div>
    <p className="mt-0.5 line-clamp-1 text-[13px] font-['PT_Sans'] text-gray-500">
      {message.body}
    </p>
    <p className="mt-0.5 truncate text-[12px] font-['PT_Sans'] text-gray-400">
      {`an ${formatRecipients(message.recipients)} · ${message.sentCount} gesendet`}
      {message.failedCount > 0 && ` · ${message.failedCount} fehlgeschlagen`}
      {/* Named the way the compose form named it, so a sent message reads back as it was written. */}
      {message.url && ` · → ${describePushTarget(message.url)}`}
    </p>
  </div>
);

export const PushMessageHistory: React.FC = () => {
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    usePushMessagesInfiniteQuery({ limit: 5 });

  const messages = data?.items ?? [];

  return (
    <section>
      <h3 className="mb-1 font-['PT_Sans'] text-[15px] font-bold text-gray-600">Verlauf</h3>

      {isLoading && (
        <div className="flex flex-col gap-2 py-2" aria-hidden="true">
          {Array.from({ length: 3 }, (_, index) => (
            <div key={index} className="h-[64px] animate-pulse rounded-lg bg-gray-100" />
          ))}
        </div>
      )}

      {!isLoading && messages.length === 0 && (
        <p className="py-2 text-sm font-['PT_Sans'] text-gray-400">
          Noch keine Nachrichten gesendet.
        </p>
      )}

      {messages.length > 0 && (
        <div className="divide-y divide-gray-200">
          {messages.map((message) => (
            <MessageRow key={message.id} message={message} />
          ))}
        </div>
      )}

      {hasNextPage && (
        <button
          type="button"
          className="mt-2 text-sm font-['PT_Sans'] text-[#004494] underline-offset-2 hover:underline disabled:opacity-50"
          onClick={() => fetchNextPage()}
          disabled={isFetchingNextPage}
        >
          {isFetchingNextPage ? "Wird geladen…" : "Mehr anzeigen"}
        </button>
      )}
    </section>
  );
};
